import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Bell, BellOff, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { updateProfile } from '@/api/endpoints/auth';
import { registerForPush, pushSupport } from '@/lib/push';
import { errorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';

/**
 * Opt-in for browser notifications. SMS and voice calls are dispatched by the
 * backend regardless of what happens here — this is an extra channel, and the copy
 * says so, because someone who declines must not believe they have turned off
 * emergency alerts.
 */
export function NotificationOptIn() {
  const { user, setUser } = useAuth();
  const [support] = useState(pushSupport);
  const enabled = Boolean(user?.push_token);

  const mutation = useMutation({
    mutationFn: async () => {
      const token = await registerForPush();
      return updateProfile({ push_token: token });
    },
    onSuccess: (updated) => {
      setUser(updated);
      toast.success('Notifications are on for this browser.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const disableMutation = useMutation({
    mutationFn: () => updateProfile({ push_token: '' }),
    onSuccess: (updated) => {
      setUser(updated);
      toast.success('Notifications turned off for this browser.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (support === 'unconfigured') {
    return (
      <p className="flex items-start gap-2 text-[13px] text-ink-secondary">
        <BellOff className="mt-0.5 size-4 shrink-0 text-ink-muted" aria-hidden />
        Browser notifications are not enabled on this deployment. Emergency SMS and calls are
        unaffected.
      </p>
    );
  }

  if (support === 'unsupported' || support === 'denied') {
    return (
      <p className="flex items-start gap-2 text-[13px] text-ink-secondary">
        <BellOff className="mt-0.5 size-4 shrink-0 text-ink-muted" aria-hidden />
        {support === 'denied'
          ? 'This browser is blocking notifications. Allow them in your browser settings to turn this on.'
          : 'This browser cannot receive push notifications.'}{' '}
        Emergency SMS and calls are unaffected.
      </p>
    );
  }

  if (enabled) {
    return (
      <div className="space-y-3">
        <p className="flex items-start gap-2 text-[13px] text-ok">
          <Check className="mt-0.5 size-4 shrink-0" aria-hidden />
          Crash alerts will appear in this browser.
        </p>
        <p className="text-[13px] text-ink-muted">
          Only your most recently registered browser receives notifications. Turning them on
          somewhere else replaces this one.
        </p>
        <Button size="sm" loading={disableMutation.isPending} onClick={() => disableMutation.mutate()}>
          Turn off
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-ink-secondary">
        Get a notification here the moment a crash is detected. This is in addition to the SMS and
        voice call sent to your registered number.
      </p>
      <Button
        variant="primary"
        size="sm"
        loading={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        <Bell className="size-4" aria-hidden />
        Enable notifications
      </Button>
    </div>
  );
}
